import { FC, ReactNode } from "react";
import Popup from "reactjs-popup";
import ClosingCrossSVG from "../components/resources/svg/functional/ClosingCrossSVG";
import { useTranslation } from "react-i18next";
import BackButtonArrowSVG from "../components/resources/svg/functional/BackButtonArrowSVG";
import useBodyOverflowHidden from "../custom-hooks/base-hooks/useBodyOverflowHidden";

interface CustomPopupProps {
    children: ReactNode;
    buttonElement?: ReactNode;
    errorElement?: ReactNode;
    title?: string;
    confirmTitle?: string;
    cancelTitle?: string;
    isVisible: boolean;
    setIsVisible: (value: boolean) => void;
    confirmHandler?: () => void;
    closeHandler?: () => void;
    closingCrossClassName?: string;
    goBackHandler?: () => void;
    onOpen?: () => void;
    wrapClassName?: string;
    titleClassName?: string;
    confirmButtonClassName?: string;
    cancelButtonClassName?: string;
    childrenWrapperClassName?: string;
    nested?: boolean;
    skipScrollUnlock?: boolean;
}

const CustomPopup: FC<CustomPopupProps> = (props) => {
    const { hidden, visible } = useBodyOverflowHidden();
    const { t } = useTranslation();
    const openHandler = () => {
        props.onOpen && props.onOpen();
        hidden();
    };
    const closeHandler = () => {
        props.setIsVisible(false);
        props.closeHandler && props.closeHandler();
        if (!props.skipScrollUnlock) {
            visible();
        }
    };
    const confirmHandler = () => {
        props.confirmHandler();
        if (!props.skipScrollUnlock) {
            visible();
        }
    };
    return (
        <>
            {props.buttonElement}
            <Popup
                onOpen={openHandler}
                open={props.isVisible}
                modal
                overlayStyle={{
                    zIndex: 99999,
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                    backdropFilter: "blur(3px)",
                    WebkitBackdropFilter: "blur(3px)",
                }}
                onClose={closeHandler}
                nested={props.nested}
            >
                <div className={`overflow-x-hidden overflow-y-auto popup-wrap ${props.wrapClassName}`}>
                    <div className="relative w-full max-h-full">
                        <div className="relative w-full">
                            <div
                                className={`flex items-center ${
                                    props.goBackHandler ? "justify-between" : "justify-end"
                                }`}
                            >
                                {props.goBackHandler && (
                                    <div
                                        onClick={props.goBackHandler}
                                        className="H20 dmax2:H16 text-textWhite flex items-center cursor-pointer"
                                    >
                                        <BackButtonArrowSVG className="dmax2:mt-[2px] mr-4 dmax2:mr-2 w-6 dmax2:w-3 h-6 dmax2:h-3" />
                                        {t("common.back2")}
                                    </div>
                                )}
                                <ClosingCrossSVG
                                    onClick={closeHandler}
                                    className={`popup-closing-cross text-textFaded hover:text-textWhite transition-all duration-300 ${props.closingCrossClassName}`}
                                />
                            </div>
                            {props.title && <h3 className={`popup-title ${props.titleClassName}`}>{props.title}</h3>}
                            <div
                                className={`flex justify-center items-center overflow-auto scrollbar-popup ${props.childrenWrapperClassName}`}
                            >
                                {props.children}
                            </div>
                            {props.errorElement}
                            <div className="flex justify-center items-center">
                                {props.confirmTitle && (
                                    <div
                                        className={`popup-button transition duration-300 ${props.confirmButtonClassName}`}
                                        onClick={confirmHandler}
                                    >
                                        {props.confirmTitle}
                                    </div>
                                )}
                                {props.cancelTitle && (
                                    <div
                                        onClick={closeHandler}
                                        className={`popup-button transition duration-300 ${props.cancelButtonClassName}`}
                                    >
                                        {props.cancelTitle}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Popup>
        </>
    );
};

export default CustomPopup;
